import { create } from "zustand";
import { ApiClientError, apiFetch, isServerUnreachable } from "../api/client";

/**
 * "ok": the API answered. "waking": no answer yet, still trying (Render free
 * cold start: 30–60 s). "unreachable": nothing for WAKE_BUDGET_MS. "offline":
 * the device has no network.
 */
export type ServerConnection = "ok" | "waking" | "unreachable" | "offline";

/** How long the first /me keeps trying before "Can't reach the server". */
export const WAKE_BUDGET_MS = 75_000;
const WAKE_RETRY_MS = 3_000;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export interface MeResponse {
  user: {
    id: string;
    email: string | null;
    phone: string | null;
    name: string | null;
    createdAt: string;
  };
  org: {
    id: string;
    name: string;
    type: "solo" | "fleet";
    timezone: string;
  };
  role: "owner" | "dispatcher" | "driver";
  driverId: string | null;
  memberships: Array<{
    orgId: string;
    role: "owner" | "dispatcher" | "driver";
    status: string;
    orgName: string;
    orgType: "solo" | "fleet";
  }>;
  /** What this server can do (absent on older APIs: assume yes). */
  features?: { documentStorage: boolean };
  plan: {
    planId: "free" | "solo" | "fleet";
    writable: boolean;
    readOnly: boolean;
    showPastDueBanner: boolean;
    seats: number;
  };
  subscription: {
    planId: string;
    status: string;
    seats: number;
    trialEndsAt: string | null;
    graceEndsAt: string | null;
    currentPeriodEnd: string | null;
  } | null;
}

interface AuthState {
  me: MeResponse | null;
  loading: boolean;
  bootstrapped: boolean;
  connection: ServerConnection;
  setMe: (me: MeResponse | null) => void;
  refreshMe: () => Promise<MeResponse | null>;
  signOut: () => Promise<void>;
}

/**
 * "Am I signed in?" via GET /api/v1/session, which answers 200 either way (a
 * 401 from /me is logged by the browser as a console error on every
 * signed-out page load). Falls back to /me if the API predates /session.
 */
async function fetchSession(): Promise<MeResponse | null> {
  try {
    const body = await apiFetch<(MeResponse & { signedIn?: boolean }) | { signedIn: false }>(
      "/api/v1/session",
      {},
      { retries: 0 },
    );
    return body && "user" in body ? (body as MeResponse) : null;
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 404) {
      return apiFetch<MeResponse>("/api/v1/me", {}, { retries: 0 }).catch((e: unknown) => {
        if (e instanceof ApiClientError && e.status === 401) return null;
        throw e;
      });
    }
    if (err instanceof ApiClientError && err.status === 401) return null;
    throw err;
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  me: null,
  loading: false,
  bootstrapped: false,
  connection: "ok",
  setMe: (me) => set({ me, bootstrapped: true }),
  refreshMe: async () => {
    set({ loading: true, connection: "waking" });
    const deadline = Date.now() + WAKE_BUDGET_MS;
    for (;;) {
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        set({ loading: false, connection: "offline" });
        return get().me;
      }
      try {
        // Each attempt: 25 s timeout (apiFetch). Through the Pages proxy a
        // sleeping API answers 504 after 8 s, so keep trying until the budget.
        const me = await fetchSession();
        set({ me, loading: false, bootstrapped: true, connection: "ok" });
        return me;
      } catch (err) {
        if (isServerUnreachable(err)) {
          if (Date.now() + WAKE_RETRY_MS < deadline) {
            await sleep(WAKE_RETRY_MS);
            continue;
          }
          // No answer is not "signed out": keep whatever session we knew.
          set({ loading: false, connection: "unreachable" });
          return get().me;
        }
        // The API answered (401 etc.): signed out.
        set({ me: null, loading: false, bootstrapped: true, connection: "ok" });
        return null;
      }
    }
  },
  signOut: async () => {
    try {
      await apiFetch("/api/v1/auth/logout", { method: "POST", body: "{}" });
    } catch {
      /* ignore */
    }
    set({ me: null });
  },
}));
